import React, { useEffect, useRef, useState } from 'react';
import { Loading } from 'carbon-components-react';
import { Stripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';

interface CheckoutStepProps {
  clientSecret: string;
  stripePromise: PromiseLike<Stripe | null>;
}

const CheckoutStep: React.FC<CheckoutStepProps> = ({
  clientSecret,
  stripePromise
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [checkoutReady, setCheckoutReady] = useState(false);

  useEffect(() => {
    setCheckoutReady(false);
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let iframeCleanup: (() => void) | undefined;

    const signalReady = () => {
      if (!cancelled) setCheckoutReady(true);
    };

    const attachToIframe = (iframe: HTMLIFrameElement) => {
      iframe.addEventListener('load', signalReady, { once: true });
      const fallback = window.setTimeout(signalReady, 2000);
      iframeCleanup = () => window.clearTimeout(fallback);
    };

    const existing = container.querySelector('iframe');
    if (existing instanceof HTMLIFrameElement) {
      attachToIframe(existing);
    } else {
      const observer = new MutationObserver(() => {
        const iframe = container.querySelector('iframe');
        if (iframe instanceof HTMLIFrameElement) {
          observer.disconnect();
          attachToIframe(iframe);
        }
      });
      observer.observe(container, { childList: true, subtree: true });

      const safety = window.setTimeout(signalReady, 15000);

      return () => {
        cancelled = true;
        observer.disconnect();
        window.clearTimeout(safety);
        iframeCleanup?.();
      };
    }

    return () => {
      cancelled = true;
      iframeCleanup?.();
    };
  }, [clientSecret]);

  return (
    <div
      ref={containerRef}
      className="bel-payment-step bel-payment-step--checkout"
    >
      {!checkoutReady && (
        <div className="bel-payment-step__loading-overlay">
          <Loading description="Loading checkout…" withOverlay={false} />
        </div>
      )}
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{ clientSecret }}
      >
        <EmbeddedCheckout className="bel-payment-step__checkout-mount" />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default CheckoutStep;
